import React, { useState, useEffect, useCallback, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Info } from "lucide-react";
import api from "config/axiosConfig";
import { InputOTP, InputOTPGroup, InputOTPSeparator, InputOTPSlot } from "ui/InputOTP";
import { awardQueryKeys } from "hooks/queries/useAwardBalanceQuery";
import { getAuthUiStrings, resolveAuthLocale } from "i18n/authUi";
import { useUser } from "util/UserContext";
import { REGEXP_ONLY_DIGITS } from "input-otp";
import { apiRoutes } from "util/apiRoutes";
import { getApiErrorMessage } from "lib/errors/normalizeApiError";

/**
 * Stable form id used to pair the email-verification step with the
 * parent AuthModal's footer submit button. The modal-footer Continue
 * button uses `<button form={EMAIL_VERIFICATION_FORM_ID} type="submit">`
 * so browser-native Enter on the OTP input submits this form.
 *
 * See `docs/architecture/forms-standard.md` section 2.
 */
export const EMAIL_VERIFICATION_FORM_ID = "auth-email-verification-form";

interface EmailVerificationModalProps {
  onClose: () => void;
  email: string;
  requestCodeOnOpen?: boolean;
  onStateChange?: (state: {
    isSubmitting: boolean;
    isButtonDisabled: boolean;
    buttonLabel: string;
  }) => void;
}

/**
 * EmailVerificationModal
 *
 * Content-only email verification step rendered inside `AuthModal`.
 * Wraps the OTP input in a real `<form>` so Enter (or completion of
 * the 6-digit code) submits via browser-native semantics.
 */
const EmailVerificationModal: React.FC<EmailVerificationModalProps> = ({
  onClose,
  email,
  requestCodeOnOpen = false,
  onStateChange,
}) => {
  const [verificationCode, setVerificationCode] = useState<string>("");
  const { refreshUser } = useUser();
  const queryClient = useQueryClient();
  const ui = getAuthUiStrings(resolveAuthLocale());
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [resendTimer, setResendTimer] = useState(requestCodeOnOpen ? 0 : 30);
  const [isResending, setIsResending] = useState(false);
  const requestedInitialCodeRef = useRef(false);

  useEffect(() => {
    let timer: ReturnType<typeof setInterval> | undefined;
    if (resendTimer > 0) {
      timer = setInterval(() => {
        setResendTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [resendTimer]);

  useEffect(() => {
    if (onStateChange) {
      onStateChange({
        isSubmitting,
        isButtonDisabled: verificationCode.length !== 6 || isSubmitting,
        // Stable label. The loading state is rendered as a centered
        // spinner by FormActions in the AuthModal footer (forms-
        // standard.md section 4 on primary-button consistency), so
        // we never send a "Verifying..." variant -- doing so would
        // double up with the spinner.
        buttonLabel: ui.emailVerificationContinue,
      });
    }
  }, [isSubmitting, onStateChange, ui.emailVerificationContinue, verificationCode.length]);

  const handleVerify = useCallback(async () => {
    if (verificationCode.length !== 6) return;
    setError(null);
    setIsSubmitting(true);
    try {
      const response = await api.post(apiRoutes.auth.emailVerify, { code: verificationCode });
      if (response.status === 200) {
        await queryClient.invalidateQueries({ queryKey: awardQueryKeys.balance() });
        await refreshUser();
        onClose();
      }
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, ui.emailVerificationFailed));
      setVerificationCode("");
    } finally {
      setIsSubmitting(false);
    }
  }, [onClose, queryClient, refreshUser, ui.emailVerificationFailed, verificationCode]);

  // Auto-submit when code is complete (preserves existing UX).
  useEffect(() => {
    if (verificationCode.length === 6 && !isSubmitting) {
      handleVerify();
    }
  }, [verificationCode, handleVerify, isSubmitting]);

  const requestVerificationCode = useCallback(async () => {
    if (isResending) {
      return;
    }
    setIsResending(true);
    setError(null);
    try {
      await api.post(apiRoutes.auth.passcode, { emailOrUsername: email, codeType: "email_verification" });
      setResendTimer(30);
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, ui.emailVerificationResendFailed));
      setResendTimer(0);
    } finally {
      setIsResending(false);
    }
  }, [email, isResending, ui.emailVerificationResendFailed]);

  useEffect(() => {
    if (!requestCodeOnOpen || requestedInitialCodeRef.current) {
      return;
    }
    requestedInitialCodeRef.current = true;
    void requestVerificationCode();
  }, [requestCodeOnOpen, requestVerificationCode]);

  const handleResend = async () => {
    if (resendTimer > 0 || isResending) return;
    await requestVerificationCode();
  };

  const formatEmail = (e: string) => {
    const [user, domain] = e.split("@");
    if (!user || !domain || user.length <= 3) return e;
    return `${user.slice(0, 3)}...@${domain}`;
  };

  const formatResendTime = (seconds: number) =>
    `${Math.floor(seconds / 60)}:${(seconds % 60).toString().padStart(2, "0")}`;

  /** Form-submit handler invoked when the parent footer Continue button submits. */
  const handleFormSubmit = (e?: React.FormEvent) => {
    if (e && typeof e.preventDefault === "function") {
      e.preventDefault();
    }
    void handleVerify();
  };

  return (
    <form
      id={EMAIL_VERIFICATION_FORM_ID}
      onSubmit={handleFormSubmit}
      className="flex flex-col w-full space-y-4"
      noValidate
    >
      <div className="flex flex-col space-y-4">
        <p className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
          {ui.emailVerificationPrompt.replace("{email}", formatEmail(email))}
        </p>

        <div className="email-verification-modal-input-otp flex justify-center">
          <InputOTP
            maxLength={6}
            pattern={REGEXP_ONLY_DIGITS}
            inputMode="numeric"
            value={verificationCode}
            autoFocus={true}
            onChange={(value) => {
              setError(null);
              setVerificationCode(value);
            }}
            containerClassName="group flex items-center"
            disabled={isSubmitting}
          >
            <InputOTPGroup>
              <InputOTPSlot index={0} />
              <InputOTPSlot index={1} />
              <InputOTPSlot index={2} />
            </InputOTPGroup>
            <InputOTPSeparator />
            <InputOTPGroup>
              <InputOTPSlot index={3} />
              <InputOTPSlot index={4} />
              <InputOTPSlot index={5} />
            </InputOTPGroup>
          </InputOTP>
        </div>

        {error && (
          <div className="flex items-center text-warning-red dark:text-warning-red-dark text-sm" role="alert">
            <Info className="mr-1 size-4" />
            <span>{error}</span>
          </div>
        )}

        <div className="flex flex-col items-center space-y-2 sm:items-start">
          <div className="text-center sm:justify-start">
            <span className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
              {ui.emailVerificationResendLead}{" "}
              {resendTimer > 0 ? (
                <span>
                  {ui.emailVerificationResendIn.replace("{time}", formatResendTime(resendTimer))}
                </span>
              ) : (
                <button
                  type="button"
                  onClick={handleResend}
                  disabled={isResending}
                  className="text-eoe-link-blue dark:text-eoe-dark-link-blue hover:underline disabled:opacity-50"
                >
                  {ui.emailVerificationResend}
                </button>
              )}
            </span>
          </div>
        </div>
      </div>
    </form>
  );
};

export default EmailVerificationModal;
